import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  planDaySchema, planRangeSchema, settingsSchema, thresholdSchema,
  type FtpDismiss, type MaxHrAccept, type MaxHrDismiss, type PlanCopy, type PlanInput, type SetPasswordRequest, type Settings, type StravaConnectRequest, type SuggestionDismiss, type ThresholdCreate, type ThresholdInput, type ThresholdKind, type ThresholdSport,
} from '@painpal/contracts';
import { ApiRequestError, apiJson, apiVoid } from '../lib/api';
import { redirectTo } from '../lib/redirect';
import { noteStravaConnect } from '../lib/strava';
import { invalidateTraining, mutationKeys, queryKeys } from './keys';
import { activityPath, PLAN_COPY_PATH, planDayPath, suggestionActionPath, thresholdPath, type SportSuggestionAction, type SuggestionAction } from './paths';

export function parseConnectUrl(input: unknown): { url: string } {
  if (typeof input === 'object' && input !== null && 'url' in input && typeof input.url === 'string') {
    return { url: input.url };
  }
  throw new ApiRequestError(502, 'bad_response', 'Strava connect response had no url');
}

export function useRetryActivity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiVoid(`${activityPath(id)}/retry`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.activities }),
  });
}

export function useDeleteActivity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiVoid(activityPath(id), { method: 'DELETE' }),
    onSuccess: (_data, id) => {
      // Drop the open detail and curve queries first: invalidating them would refetch a 404 and unmount the page before it navigates.
      queryClient.removeQueries({ queryKey: queryKeys.activity(id) });
      queryClient.removeQueries({ queryKey: [...queryKeys.powerCurves, id] });
      return invalidateTraining(queryClient);
    },
  });
}

export function useCreateThreshold(kind: ThresholdKind, sport: ThresholdSport = 'ride') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: mutationKeys.thresholdChange(sport, kind),
    mutationFn: (input: ThresholdCreate) => apiJson('/api/thresholds', thresholdSchema, { method: 'POST', json: input }),
    onSuccess: () => invalidateTraining(queryClient),
  });
}

export function useUpdateThreshold(kind: ThresholdKind, sport: ThresholdSport = 'ride') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: mutationKeys.thresholdChange(sport, kind),
    mutationFn: ({ id, input }: { id: string; input: ThresholdInput }) =>
      apiJson(thresholdPath(id), thresholdSchema, { method: 'PATCH', json: input }),
    onSuccess: () => invalidateTraining(queryClient),
  });
}

export function useDeleteThreshold(kind: ThresholdKind, sport: ThresholdSport = 'ride') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: mutationKeys.thresholdChange(sport, kind),
    mutationFn: (id: string) => apiVoid(thresholdPath(id), { method: 'DELETE' }),
    onSuccess: () => invalidateTraining(queryClient),
  });
}

export function useStravaSync() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiVoid('/api/strava/sync', { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.strava }),
  });
}

export function useStravaConnect() {
  return useMutation({
    mutationFn: (request: StravaConnectRequest) => apiJson('/api/strava/connect', { parse: parseConnectUrl }, { method: 'POST', json: request }),
    onSuccess: ({ url }) => {
      noteStravaConnect();
      redirectTo(url);
    },
  });
}

export function useDisconnectStrava() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiVoid('/api/strava', { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.strava }),
  });
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiVoid('/api/account', { method: 'DELETE' }),
    onSuccess: () => queryClient.clear(),
  });
}

export function recordConsent(): Promise<void> {
  return apiVoid('/api/account/consent', { method: 'POST' });
}

export function useConsent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: recordConsent,
    // Refetched, not just invalidated: the consent gate reads the cached account, and nothing on screen observes it.
    onSuccess: () => queryClient.refetchQueries({ queryKey: queryKeys.account }),
  });
}

export function useSetPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: SetPasswordRequest) => apiVoid('/api/account/password', { method: 'POST', json: request }),
    onSuccess: () => Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.account }),
      queryClient.invalidateQueries({ queryKey: queryKeys.session }),
    ]),
  });
}

export function useUpdateSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Settings) => apiJson('/api/settings', settingsSchema, { method: 'PATCH', json: input }),
    onSuccess: (settings) => {
      queryClient.setQueryData(queryKeys.settings, settings);
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.zones }),
        queryClient.invalidateQueries({ queryKey: queryKeys.activities }),
      ]);
    },
  });
}

type SuggestionBody = FtpDismiss | MaxHrAccept | MaxHrDismiss | SuggestionDismiss;

function useSuggestionPost<Variables>(
  action: SuggestionAction, toJson: (variables: Variables) => SuggestionBody | undefined, mutationKey?: readonly string[],
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey,
    mutationFn: (variables: Variables) => apiVoid(suggestionActionPath(action), { method: 'POST', json: toJson(variables) }),
    onSuccess: () => invalidateTraining(queryClient),
    // A conflict means the offer went stale (gone, or its date already taken): re-read so it stops being offered.
    onError: (error) => {
      if (error instanceof ApiRequestError && error.status === 409) void queryClient.invalidateQueries({ queryKey: queryKeys.thresholdSuggestions });
    },
  });
}

export const useAcceptFtpSuggestion = () => useSuggestionPost<void>('ftp/accept', () => undefined, mutationKeys.thresholdChange('ride', 'ftp'));

// Dismissals send what the athlete saw, so anything newer stays offered.
export const useDismissFtpSuggestion = () => useSuggestionPost<number>('ftp/dismiss', (strava) => ({ strava }));

export const useAcceptMaxHrSteps = () => useSuggestionPost<string[]>('max-hr/accept', (activityIds) => ({ activityIds }), mutationKeys.thresholdChange('ride', 'max_hr'));

export const useDismissMaxHrSuggestion = () => useSuggestionPost<string[]>('max-hr/dismiss', (activityIds) => ({ activityIds }));

const ACTION_THRESHOLD: Record<SportSuggestionAction, [ThresholdSport, ThresholdKind]> = {
  'run-pace': ['run', 'threshold_pace'],
  'run-lthr': ['run', 'lthr'],
  'other-lthr': ['other', 'lthr'],
};

export const useAcceptSportSuggestion = (action: SportSuggestionAction) =>
  useSuggestionPost<void>(`${action}/accept`, () => undefined, mutationKeys.thresholdChange(...ACTION_THRESHOLD[action]));

// The value shown, so a different suggestion arriving later stays offered.
export const useDismissSportSuggestion = (action: SportSuggestionAction) =>
  useSuggestionPost<number>(`${action}/dismiss`, (value) => ({ value }));

// The forecast on the Fitness chart follows the plan, so every plan change refreshes Fitness too.
function invalidatePlan(queryClient: QueryClient): Promise<unknown> {
  return Promise.all([queryKeys.plan, queryKeys.fitness].map((queryKey) => queryClient.invalidateQueries({ queryKey })));
}

export function useSavePlanDay() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ date, input }: { date: string; input: PlanInput }) =>
      apiJson(planDayPath(date), planDaySchema, { method: 'PUT', json: input }),
    onSuccess: () => invalidatePlan(queryClient),
  });
}

export function useClearPlanDay() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (date: string) => apiVoid(planDayPath(date), { method: 'DELETE' }),
    onSuccess: () => invalidatePlan(queryClient),
  });
}

export function useCopyPlanWeek() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (copy: PlanCopy) => apiJson(PLAN_COPY_PATH, planRangeSchema, { method: 'POST', json: copy }),
    // A refusal (409) means the week gained blocks elsewhere; refetch so they show.
    onSettled: () => invalidatePlan(queryClient),
  });
}
